import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Calendar, SlidersHorizontal, Loader } from 'lucide-react';
import { RootState } from '../store/reducers';
import { fetchDecisionsRequest } from '../store/decisions/actions';
import './Recherche.css';

const statutLabels: Record<string, { label: string; color: string; bg: string }> = {
  brouillon:  { label: 'Brouillon',   color: '#6B7280', bg: '#F3F4F6' },
  a_verifier: { label: 'À vérifier',  color: '#C4A535', bg: '#FFF8E7' },
  valide:     { label: 'Validé',       color: '#047857', bg: '#ECFDF5' },
  anonymise:  { label: 'Anonymisé',    color: '#2563EB', bg: '#EFF6FF' },
  publie:     { label: 'Publié',       color: '#059669', bg: '#D1FAE5' },
};

const suggestions = [
  'Bail commercial',
  'Divorce',
  'Droit foncier',
  'Contrat de travail',
  'Responsabilité civile',
];

const periodeFilter = ['Toute période', 'Dernière semaine', 'Dernier mois', '3 derniers mois', '6 derniers mois', 'Année en cours'];

const Recherche: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { decisions, count, isLoading } = useSelector((state: RootState) => state.decisions);

  const [query, setQuery] = useState('');
  const [selectedJuridiction, setSelectedJuridiction] = useState('');
  const [selectedPeriode, setSelectedPeriode] = useState('Toute période');
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = () => {
    if (query.trim()) {
      setHasSearched(true);
      const params: any = { search: query, page: 1, page_size: 20 };
      if (selectedJuridiction) params.juridiction = selectedJuridiction;
      dispatch(fetchDecisionsRequest(params));
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setHasSearched(true);
    dispatch(fetchDecisionsRequest({ search: suggestion, page: 1, page_size: 20 }));
  };

  const getParties = (d: any) => {
    const parts = [];
    if (d.demandeur) parts.push(d.demandeur);
    if (d.defendeur) parts.push(d.defendeur);
    return parts.join(' c/ ') || '—';
  };

  return (
    <div className="recherche-page">
      {/* Search header */}
      <div className="recherche-header">
        <h2 className="recherche-title">Recherche dans la jurisprudence</h2>
        <div className="recherche-bar">
          <div className="recherche-input-wrapper">
            <Search size={18} className="recherche-input-icon" />
            <input
              type="text"
              placeholder="Rechercher par mots-clés, numéro de décision, parties..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <button className="btn-recherche" onClick={handleSearch} disabled={isLoading}>
            {isLoading ? 'Recherche...' : 'Rechercher'}
          </button>
        </div>

        {/* Suggestions */}
        {!hasSearched && (
          <div className="recherche-suggestions">
            <span className="suggestions-label">Suggestions :</span>
            {suggestions.map((s, i) => (
              <button key={i} className="suggestion-chip" onClick={() => handleSuggestionClick(s)}>
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="recherche-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Filtrer par juridiction..."
            value={selectedJuridiction}
            onChange={(e) => setSelectedJuridiction(e.target.value)}
            className="recherche-select"
          />

          <div className="recherche-select-wrapper">
            <Calendar size={14} className="select-icon" />
            <select
              value={selectedPeriode}
              onChange={(e) => setSelectedPeriode(e.target.value)}
              className="recherche-select with-icon"
            >
              {periodeFilter.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          <button className="btn-filter-advanced">
            <SlidersHorizontal size={14} /> Filtres avancés
          </button>
        </div>
      </div>

      {/* Results */}
      {hasSearched && (
        <div className="recherche-results">
          {isLoading ? (
            <div className="recherche-loading">
              <Loader size={28} className="recherche-spinner" />
              <span>Recherche en cours...</span>
            </div>
          ) : (
            <>
              <p className="recherche-results-count">
                {count} résultat{count > 1 ? 's' : ''} pour « {query} »
              </p>

              {decisions.length > 0 ? (
                <div className="recherche-results-list">
                  {decisions.filter(d => d.statut !== 'brouillon' && d.date_decision).map(d => {
                    const s = statutLabels[d.statut] || { label: d.statut, color: '#666', bg: '#f3f4f6' };
                    return (
                      <div
                        key={d.id}
                        className="result-card"
                        onClick={() => navigate(`/espace-tca/decisions/${d.id}`)}
                      >
                        <div className="result-card-header">
                          <span className="result-numero">{d.numero || d.id}</span>
                          <span className="status-badge" style={{ color: s.color, background: s.bg }}>
                            {s.label}
                          </span>
                        </div>
                        <div className="result-card-meta">
                          <span>{d.juridiction || '—'}</span>
                          <span className="meta-dot">·</span>
                          <span>{d.date_decision ? new Date(d.date_decision).toLocaleDateString('fr-FR') : '—'}</span>
                        </div>
                        <div className="result-tags">
                          {d.matiere && <span className="result-tag">{d.matiere.toLowerCase()}</span>}
                          {d.juridiction && <span className="result-tag">{d.juridiction}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="recherche-empty">
                  Aucun résultat trouvé pour votre recherche. Essayez d'élargir vos critères.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Recherche;
